import { contentfulNextImageLoader } from '@util/images';
import NextImage, { ImageProps } from 'next/legacy/image';
import type { StaticImageData } from 'next/image';
// import log from '@util/logging';  // ***TODO: find out if log is being removed from Platform

import theme from '@design/theme';

type EVImgProps = {
  imageFocalPointData?: any; //if imageFocalPointData is supplied, use it should be passed the whole object
  src?: string | StaticImageData; //if supplied, it will override the imageFocalPointData
  imageDesc?: string; //if supplied, it will override the imageFocalPointData
  fill?: boolean;
  objectFit?: string;
  objectPostion?: string;
  sizes?: Stack<string>;
  dataTestId?: string;
} & Partial<Omit<ImageProps, 'sizes'>>;

type Stack<T> = T | (T | null)[];

// TODO: cover this in tests, should be easy
export const buildSizesString = (sizes?: Stack<string | null>): string | undefined => {
  if (!sizes) {
    return;
  }
  // normalize single entries to single element arrays
  const sizeArr = (Array.isArray(sizes) ? sizes : [sizes]).slice(0, theme.breakpoints.length + 1);
  // map larger breakpoints, which are specified by a query
  const breakpointSizes = [...theme.breakpoints]
    .map((breakpoint, i) => {
      const size = sizeArr[i + 1];
      if (size) {
        return `(min-width: ${breakpoint}) ${size}`;
      }
    })
    // parse out empty sizes
    .filter((a) => !!a);
  const defaultSize = sizeArr[0];

  // now map smallest size as default.
  if (defaultSize) {
    breakpointSizes.push(`${defaultSize}`);
  }
  return breakpointSizes.join(', ');
};

const EVImg = ({
  imageFocalPointData,
  src,
  imageDesc,
  sizes,
  objectPosition,
  dataTestId,
  ...props
}: EVImgProps) => {
  //start with an empty src variable
  //define each as a string
  let imgUrl: any = '';
  let desc = '';
  let position = objectPosition || '50% 50%';
  let imgWidth: number | undefined;
  let imgHeight: number | undefined;

  // console.log('imageFocalPointData', imageFocalPointData);
  //check if "imageFocalPointData" was supplied
  if (imageFocalPointData) {
    //set src as the url of the image
    imgUrl = imageFocalPointData?.fields?.image?.fields?.file?.url || imageFocalPointData?.image;
    desc = imageFocalPointData?.fields?.image?.fields?.description;
    imgWidth = imageFocalPointData?.fields?.image?.fields?.file?.details?.image?.width;
    imgHeight = imageFocalPointData?.fields?.image?.fields?.file?.details?.image?.height;
    let focalPointX = imageFocalPointData?.fields?.focalPoint?.focalPoint?.x;
    let focalPointY = imageFocalPointData?.fields?.focalPoint?.focalPoint?.y;

    //divide focalPointX by imgWidth to get percentage for horizontal image postion percentage
    let horizontalPosition = focalPointX / imgWidth!;
    //divide focalPointY by imgHeight to get percentage for vertical image postion percentage
    let verticalPosition = focalPointY / imgHeight!;
    position = `${Math.ceil(horizontalPosition * 100)}% ${Math.ceil(verticalPosition * 100)}%`;
  }

  //if "src" is defined, override the imgUrl
  if (src) {
    imgUrl = src;
  }

  //if "imageDesc" is defined, override the desc
  if (imageDesc) {
    desc = imageDesc;
  }

  //if imgUrl is empty, return null
  if (!imgUrl) {
    // log.warn('EVImg: missing imageData prop');
    return null;
  }

  if (typeof imgUrl == 'object') {
    imgUrl = imgUrl.fields.file.url;
  }

  // check imgUrl is a relative url, if so, add a "https://" to it
  // because next/image doesn't like relative urls that Contentful supplies
  // TODO: find more elegant solution
  if (/^\/\//.test(imgUrl)) {
    imgUrl = `https:${imgUrl}`;
  }

  const sizeStr = buildSizesString(sizes);

  // check imgUrl is a supplied from Contentful and next/image with the separate loader
  if (typeof imgUrl == 'string' && imgUrl.startsWith('https://images.ctfassets.net/')) {
    // check if string ends in .gif
    if (imgUrl.endsWith('.gif')) {
      // use default loader for gifs
      return (
        <NextImage
          src={imgUrl}
          width={imgWidth}
          height={imgHeight}
          objectPosition={position}
          alt={desc}
          unoptimized={true}
          layout="fill"
          sizes="100vw"
          objectFit="cover"
          data-testid={dataTestId}
          {...props}
        />
      );
    } else {
      return (
        <NextImage
          src={imgUrl}
          loader={contentfulNextImageLoader}
          alt={desc}
          objectPosition={position}
          layout="fill"
          objectFit="cover"
          // null when no sizes prop passed
          sizes={sizeStr}
          data-testid={dataTestId}
          {...props}
        />
      );
    }
  }
  return (
    <NextImage
      src={imgUrl}
      alt={desc}
      objectPosition={position}
      layout="fill"
      objectFit="cover"
      // null when no sizes prop passed
      sizes={sizeStr}
      data-testid={dataTestId}
      {...props}
    />
  );
  // TODO: rewrite to attach Contentful image data to NextImage
  // return <img/>;
};

export default EVImg;
